import React, { useState, useEffect } from "react";
import ApiCall from "../constants/Api";
import { helpAxios } from "../helpers/helpAxios";
import { errorHandler } from "../components/ErrorHandler";
import FormLogin from "../components/FormLogin";
import { Dashboard } from "./Dashboard";
import "../App.css";
import SiteConfig from "../constants/SiteConfig";
import { ErrorSessionNoLongerExists } from "../utilities/ErrorSessionNoLongerExists";
import ErrorCodes from "../constants/ErrorCodes";
import ChangeUserPassword from "../utilities/ChangeUserPassword";
import { successMsg } from "../components/SuccessHandler";
import { WebSocketProvider } from "../services/WebSocketProvider";
import MessagesConfig from "../constants/MessagesConfig";

const Login = () => {
  let loginInit = JSON.parse(localStorage.getItem("loginInfo")) || {
    Session: "",
  };

  const [loginInfo, setLoginInfo] = useState(loginInit);
  const [sessionNoLongerExists, setSessionNoLongerExists] = useState(false);
  let loginUUID = localStorage.getItem("UUID") || "";
  const [loginInfoUUID, setLoginInfoUUID] = useState(loginUUID);
  const [pingStatus, setPingStatus] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogPingVisible, setDialogPingVisible] = useState(false);
  const [change, setChange] = useState(false);
  const [changePasswordStatus, setChangePasswordStatus] = useState(false);
  const [newUserPassword, setNewUserPassword] = useState({
    user: "",
    password: "",
  });

  useEffect(() => {
    if (changePasswordStatus) {
      successMsg("Password has been changed successfully");
      setChangePasswordStatus(false);
    }
  }, [changePasswordStatus]);

  const onlineResetUserPassword = async (req) => {
    let res;
    try {
      res = await helpAxios(ApiCall.ONLINE_RESET_USER_PASSWORD, req);

      // Show error message
      if (res.OutParams.Result !== 0) {
        errorHandler(res.error);
      }
    } catch (error) {
      errorHandler(error.toString());
    }
    return res;
  };

  //Call service method LOGIN USER
  const userLogin = (form) => {
    setDialogPingVisible(true);
    setPingStatus(false);

    const fetchData = async () => {
      const isPingSuccessful = await validatePing();

      if (!isPingSuccessful) {
        setPingStatus(false);
        setLoginInfo({ Session: "" });
        setLoginInfoUUID("");
      } else {
        setPingStatus(true);

        // Function for consuming the GetMyIP method
        const resIP = await helpAxios(ApiCall.GET_MY_IP, {});
        if (resIP.error) {
          let errorDet;
          if (resIP.errorDetails) {
            errorDet = resIP.errorDetails;
          }
          setPingStatus(false);
          errorHandler(resIP.error, errorDet);
        } else {
          const { userName, password } = form;
          const res = await helpAxios(ApiCall.LOGIN, {
            LoginName: userName,
            Password: password,
            PhoneLine: SiteConfig.LOGIN_PHONE_LINE,
            IP: resIP.IP,
            System: SiteConfig.LOGIN_SYSTEM_NAME,
          });

          if (res.error) {
            if (pingStatus) {
              setDialogPingVisible(false);
            }

            errorHandler(res.error);
            if (res.data.ResetPassword) {
              ChangeUserPassword(
                userName,
                password,
                onlineResetUserPassword,
                setChangePasswordStatus,
                setNewUserPassword,
              );
            }
          } else {
            setLoginInfo({
              ...res,
              Session: res.UUID,
            });
            setLoginInfoUUID(res.UUID);
            setPingStatus(true);
            localStorage.setItem("publicIP", resIP.IP);

            //Set user name
            localStorage.setItem("userName", userName);
            localStorage.setItem("UserRights", JSON.stringify(res.UserRights));
          }
        }
      }
    };

    fetchData();
  };

  //Call service method LOGOUT USER
  const userLogout = () => {
    setDialogPingVisible(false);
    const fetchData = async () => {
      const res = await helpAxios(ApiCall.LOGOUT, {}, loginInfo.Session);
      if (res.error) {
        errorHandler(res.error);

        if (
          res.Result === ErrorCodes.InvalidSession ||
          res.Result === ErrorCodes.SessionNoLongerExists
        ) {
          if (sessionNoLongerExists === "false") {
            errorHandler(res.error);
            setLoginInfoUUID("");
          }
          setSessionNoLongerExists(true);
        } else {
          errorHandler(res.error);
          setLoginInfoUUID("");
        }
      } else {
        setLoginInfo({ Session: "" });
        setLoginInfoUUID("");
        localStorage.clear();
      }
    };

    fetchData();
  };

  useEffect(() => {
    localStorage.setItem("loginInfo", JSON.stringify(loginInfo));
  }, [loginInfo]);

  useEffect(() => {
    if (sessionNoLongerExists) {
      ErrorSessionNoLongerExists();
      const modalBackdrop = document.querySelector(".modal-backdrop");

      setLoginInfo({ Session: "" });
      setLoginInfoUUID("");

      if (modalBackdrop) {
        modalBackdrop.parentNode.removeChild(modalBackdrop);
      }
    }
  }, [sessionNoLongerExists]);

  useEffect(() => {
    if (change) {
      if (localStorage.getItem("UUID")) {
        setPingStatus(true);
      } else {
        setPingStatus(false);
        setLoginInfo({ Session: "" });
        setLoginInfoUUID("");
        localStorage.clear();
      }
      setIsLoading(false);
      setChange(false);
    }
  }, [change]);

  useEffect(() => {
    //This handler if for the opened tab directed from the portal
    const handleMessage = async (e) => {
      const { data } = e;
      if (
        data?.type === MessagesConfig.SessionMessageTypes.REQUEST_SESSION_STATUS
      ) {
        e.source?.postMessage(
          {
            type: MessagesConfig.SessionMessageTypes.SESSION_STATUS,
            payload: { status: MessagesConfig.MessageStatus.OK },
          },
          e.origin,
        );
        return;
      }
      if (data?.type === MessagesConfig.SessionMessageTypes.SESSION_INIT) {
        setPingStatus(true);
        setLoginInfo({
          UUID: data.payload.UUID,
          Session: data.payload.UUID,
        });
        setLoginInfoUUID(data.payload.UUID);
        localStorage.setItem("UUID", data.payload.UUID);
        localStorage.setItem("userName", data.payload.User);
        localStorage.setItem(
          "UserRights",
          JSON.stringify(data.payload.UserRights),
        );
      }
    };

    //This handler is for the already opened tabs, to update the UUID
    const handleStorageChange = async (event) => {
      if (event.key === "UUID") {
        if (event.newValue === "") {
          setPingStatus(false);
          setDialogPingVisible(false);
        } else {
          setLoginInfoUUID(event.newValue);
          setPingStatus(true);
          setLoginInfo({
            OutParams: {
              Result: 0,
              ErrParams: "",
              ErrMsg: "",
            },
            UUID: event.newValue,
            Session: event.newValue,
          });
        }
      }
    };

    setChange(true);

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("UUID", loginInfoUUID);
  }, [loginInfoUUID]);

  //Services Ping
  const Ping = async (methodName, serviceName, isProxy) => {
    const response = isProxy
      ? await helpAxios(methodName)
      : await helpAxios(ApiCall.INVOKE_GET_METHOD, {
          MethodName: methodName,
          ServiceName: serviceName,
        });

    if (!response) return;
    return response;
  };

  //Validate Services
  const validatePing = async () => {
    try {
      //PROXY PING
      const resProxySec = await Ping(ApiCall.PING, "", true);
      if (resProxySec && resProxySec.error) {
        const resLegacy = await Ping(ApiCall.PING_LEGACY, "", true);
        if (resLegacy && resLegacy.error) {
          return false;
        }
      }

      //USER ACCESS PING
      const resPingUserAccess = await Ping(
        ApiCall.PING,
        SiteConfig.USER_ACCESS_SVC,
      );
      if (!resPingUserAccess || resPingUserAccess.error) {
        resPingUserAccess = await Ping(
          ApiCall.PING_LEGACY,
          SiteConfig.USER_ACCESS_SVC,
        );
        if (!resPingUserAccess || resPingUserAccess.error) {
          return false;
        }
      }

      const resPingRacingMgmt = await Ping(
      ApiCall.PING,
      SiteConfig.RACING_MGMT_SVC,
      );
      if (!resPingRacingMgmt || resPingRacingMgmt.error) {
        resPingRacingMgmt = await Ping(
          ApiCall.PING_LEGACY,
          SiteConfig.RACING_MGMT_SVC,
        );
        if (!resPingRacingMgmt || resPingRacingMgmt.error) {
          return false;
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  return (
    <>
      {isLoading ? (
        <div></div>
      ) : (
        <>
          {!pingStatus || !loginInfoUUID ? (
            <>
              <div className="" style={{ backgroundColor: "#f4f7fa" }}>
                <FormLogin
                  loginCall={userLogin}
                  pingStatus={pingStatus}
                  dialogPingVisible={dialogPingVisible}
                  setDialogPingVisible={setDialogPingVisible}
                  newUserPassword={newUserPassword}
                />
              </div>
            </>
          ) : (
            <WebSocketProvider>
              <Dashboard
                data={loginInfo}
                logout={userLogout}
                setLoginInfo={setLoginInfo}
                setSessionNoLongerExists={setSessionNoLongerExists}
              />
            </WebSocketProvider>
          )}
        </>
      )}
    </>
  );
};

export default Login;
